import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Car, MapPin, Clock, DollarSign, User, Phone, Calendar,
  Navigation, Check, X, AlertCircle, Play, Square, 
  ChevronRight, Star, RefreshCw, CheckCircle, Timer, 
  Route, Radio, ArrowLeft, Home, Bell, Settings, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

// Status configurations
const RIDE_STATUS = {
  requested: { label: 'Available', color: 'bg-green-500', textColor: 'text-green-400', action: 'Accept' },
  pending_driver: { label: 'Pending', color: 'bg-yellow-500', textColor: 'text-yellow-400', action: 'Accept' },
  driver_assigned: { label: 'Assigned', color: 'bg-purple-500', textColor: 'text-purple-400', action: 'Navigate' },
  en_route_pickup: { label: 'En Route', color: 'bg-blue-500', textColor: 'text-blue-400', action: 'Arrived' },
  arrived_pickup: { label: 'At Pickup', color: 'bg-cyan-500', textColor: 'text-cyan-400', action: 'Start Ride' },
  in_progress: { label: 'In Progress', color: 'bg-orange-500', textColor: 'text-orange-400', action: 'Complete' },
  completed: { label: 'Completed', color: 'bg-green-600', textColor: 'text-green-400', action: null },
  cancelled: { label: 'Cancelled', color: 'bg-red-500', textColor: 'text-red-400', action: null }
};

const DriverRidesPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [activeView, setActiveView] = useState('today'); // 'today', 'available', 'history'
  const [todayRides, setTodayRides] = useState([]);
  const [availableRides, setAvailableRides] = useState([]);
  const [completedRides, setCompletedRides] = useState([]);
  const [stats, setStats] = useState({ today: 0, earnings: 0, rating: 0 });
  const [isOnline, setIsOnline] = useState(true);
  const [loading, setLoading] = useState(true);
  const [selectedRide, setSelectedRide] = useState(null);
  const [processingAction, setProcessingAction] = useState(false);
  const [gpsPosition, setGpsPosition] = useState(null);

  // Redirect if not authenticated or not a driver
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // Fetch GPS position
  const getCurrentPosition = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGpsPosition({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.error('GPS error:', error);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    }
  }, []);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const [myRidesRes, availableRes] = await Promise.all([
        api.get('/rides/driver/my-rides'),
        api.get('/rides/driver/available')
      ]);

      const myRides = myRidesRes.data || [];
      const today = new Date().toDateString();
      
      // Filter today's rides
      const todayFiltered = myRides.filter(r => {
        const rideDate = new Date(r.scheduled_pickup || r.created_at).toDateString();
        return rideDate === today && r.status !== 'completed' && r.status !== 'cancelled';
      });
      
      // Completed rides
      const completed = myRides.filter(r => r.status === 'completed');
      
      setTodayRides(todayFiltered);
      setAvailableRides(availableRes.data || []);
      setCompletedRides(completed);
      
      // Calculate stats
      const todayEarnings = completed
        .filter(r => new Date(r.completed_at || r.updated_at).toDateString() === today)
        .reduce((sum, r) => sum + (r.driver_earnings || 0), 0);
      
      setStats({
        today: todayFiltered.length + completed.filter(r => new Date(r.completed_at || r.updated_at).toDateString() === today).length,
        earnings: todayEarnings,
        rating: user?.average_rating || 4.8
      });
    } catch (error) {
      console.error('Error fetching rides:', error);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchData();
    getCurrentPosition();
    
    // Poll for updates every 30 seconds
    const interval = setInterval(() => {
      fetchData();
      if (isOnline) getCurrentPosition();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchData, getCurrentPosition, isOnline]);

  // Action handlers
  const acceptRide = async (ride) => {
    setProcessingAction(true);
    try {
      await api.put(`/rides/${ride.id}/accept`);
      toast.success('Ride accepted! Navigate to pickup location.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept ride');
    }
    setProcessingAction(false);
  };

  const updateRideStatus = async (rideId, newStatus) => {
    setProcessingAction(true);
    try {
      const payload = { status: newStatus };
      if (gpsPosition) {
        payload.driver_location = gpsPosition;
      }
      await api.put(`/rides/${rideId}/status`, null, { params: { status: newStatus } });
      
      const statusMessages = {
        en_route_pickup: 'On your way to pickup!',
        arrived_pickup: 'Arrived at pickup. Waiting for passenger.',
        in_progress: 'Ride started!',
        completed: 'Ride completed! Great job!'
      };
      toast.success(statusMessages[newStatus] || 'Status updated');
      fetchData();
      setSelectedRide(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
    setProcessingAction(false);
  };

  const openNavigation = (ride, destination = 'pickup') => {
    const location = destination === 'pickup' ? ride.pickup_location : ride.dropoff_location;
    const address = `${location.address}, ${location.city}, ${location.state} ${location.zip_code}`;
    const encodedAddress = encodeURIComponent(address);
    
    // Try to open in maps app
    if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      window.open(`maps://maps.apple.com/?daddr=${encodedAddress}`, '_blank');
    } else {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`, '_blank');
    }
  };

  const callCustomer = (ride) => {
    if (ride.customer_phone) {
      window.location.href = `tel:${ride.customer_phone}`;
    } else {
      toast.error('Customer phone not available');
    }
  };

  // Render ride card
  const renderRideCard = (ride, showAccept = false) => {
    const status = RIDE_STATUS[ride.status] || RIDE_STATUS.requested;
    const isActive = ['driver_assigned', 'en_route_pickup', 'arrived_pickup', 'in_progress'].includes(ride.status);
    
    return (
      <motion.div
        key={ride.id}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`bg-[#111111] border rounded-2xl p-4 mb-4 ${
          isActive ? 'border-[#8B5CF6] shadow-lg shadow-[#8B5CF6]/20' : 'border-white/10'
        }`}
        onClick={() => setSelectedRide(ride)}
        data-testid={`ride-card-${ride.id}`}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 ${status.color} rounded-full flex items-center justify-center`}>
              <Car className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="font-semibold text-white text-lg">{ride.customer_name || 'Customer'}</p>
              <p className="text-xs text-gray-500 font-mono">{ride.reference}</p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${status.color} text-white`}>
            {status.label}
          </span>
        </div>
        
        {/* Locations */}
        <div className="space-y-3 mb-4">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <MapPin className="w-3 h-3 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-green-400 font-medium">PICKUP</p>
              <p className="text-white">{ride.pickup_location?.address}</p>
              <p className="text-gray-500 text-sm">{ride.pickup_location?.city}, NC</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <MapPin className="w-3 h-3 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-red-400 font-medium">DROPOFF</p>
              <p className="text-white">{ride.dropoff_location?.address}</p>
              <p className="text-gray-500 text-sm">{ride.dropoff_location?.city}, NC</p>
            </div>
          </div>
        </div>
        
        {/* Stats Row */}
        <div className="flex items-center justify-between pt-3 border-t border-white/10">
          <div className="flex items-center gap-4 text-sm">
            <span className="flex items-center gap-1 text-gray-400">
              <Clock className="w-4 h-4" />
              {ride.estimated_duration_minutes || 30} min
            </span>
            <span className="flex items-center gap-1 text-gray-400">
              <Route className="w-4 h-4" />
              {ride.estimated_miles || ride.distance_miles || '--'} mi
            </span>
            {ride.payment_method === 'cash' && (
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-500/20 text-amber-400">
                💵 Cash
              </span>
            )}
          </div>
          <div className="text-right">
            <p className="text-xl font-bold text-green-400">
              ${(ride.driver_earnings || (ride.estimated_fare || 0) * 0.85).toFixed(2)}
            </p>
            <p className="text-xs text-gray-500">Your earnings</p>
          </div>
        </div>
        
        {/* Action Button for Available Rides */}
        {showAccept && (
          <Button 
            className="w-full mt-4 bg-green-600 hover:bg-green-700 text-white text-lg py-6"
            onClick={(e) => { e.stopPropagation(); acceptRide(ride); }}
            disabled={processingAction}
            data-testid={`accept-ride-${ride.id}`}
          >
            {processingAction ? (
              <RefreshCw className="w-5 h-5 animate-spin mr-2" />
            ) : (
              <Check className="w-5 h-5 mr-2" />
            )}
            Accept Ride
          </Button>
        )}
      </motion.div>
    );
  };

  // Render selected ride detail modal
  const renderRideModal = () => {
    if (!selectedRide) return null;
    
    const status = RIDE_STATUS[selectedRide.status] || RIDE_STATUS.requested;
    
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 z-50 flex items-end"
          onClick={() => setSelectedRide(null)}
        >
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            className="w-full bg-[#111111] rounded-t-3xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle */}
            <div className="sticky top-0 bg-[#111111] pt-3 pb-4 px-6 border-b border-white/10">
              <div className="w-12 h-1 bg-gray-600 rounded-full mx-auto mb-4"></div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xl font-bold text-white">{selectedRide.customer_name}</p>
                  <p className="text-sm text-gray-500 font-mono">{selectedRide.reference}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-bold ${status.color} text-white`}>
                  {status.label}
                </span>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Quick Actions */}
              <div className="grid grid-cols-3 gap-3">
                <Button
                  variant="outline"
                  className="flex flex-col items-center py-4 h-auto border-white/20 text-white hover:bg-white/10"
                  onClick={() => callCustomer(selectedRide)}
                >
                  <Phone className="w-6 h-6 mb-1 text-green-400" />
                  <span className="text-xs">Call</span>
                </Button>
                <Button
                  variant="outline"
                  className="flex flex-col items-center py-4 h-auto border-white/20 text-white hover:bg-white/10"
                  onClick={() => openNavigation(selectedRide, selectedRide.status === 'in_progress' ? 'dropoff' : 'pickup')}
                >
                  <Navigation className="w-6 h-6 mb-1 text-blue-400" />
                  <span className="text-xs">Navigate</span>
                </Button>
                <Button
                  variant="outline"
                  className="flex flex-col items-center py-4 h-auto border-white/20 text-white hover:bg-white/10"
                  onClick={() => window.location.href = `sms:${selectedRide.customer_phone}`}
                >
                  <Radio className="w-6 h-6 mb-1 text-purple-400" />
                  <span className="text-xs">Text</span>
                </Button>
              </div>

              {/* Locations */}
              <div className="space-y-4">
                <div className="bg-[#0a0a0a] rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-green-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-green-400 font-medium mb-1">PICKUP LOCATION</p>
                      <p className="text-white font-medium">{selectedRide.pickup_location?.address}</p>
                      <p className="text-gray-500">{selectedRide.pickup_location?.city}, NC {selectedRide.pickup_location?.zip_code}</p>
                      {selectedRide.pickup_location?.notes && (
                        <p className="text-blue-400 text-sm mt-2">📝 {selectedRide.pickup_location.notes}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-[#0a0a0a] rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-red-500/20 rounded-xl flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-red-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-red-400 font-medium mb-1">DROPOFF LOCATION</p>
                      <p className="text-white font-medium">{selectedRide.dropoff_location?.address}</p>
                      <p className="text-gray-500">{selectedRide.dropoff_location?.city}, NC {selectedRide.dropoff_location?.zip_code}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Ride Info */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-[#0a0a0a] rounded-xl p-4 text-center">
                  <Route className="w-6 h-6 text-[#8B5CF6] mx-auto mb-2" />
                  <p className="text-xl font-bold text-white">{selectedRide.estimated_miles || '--'}</p>
                  <p className="text-xs text-gray-500">Miles</p>
                </div>
                <div className="bg-[#0a0a0a] rounded-xl p-4 text-center">
                  <Clock className="w-6 h-6 text-[#8B5CF6] mx-auto mb-2" />
                  <p className="text-xl font-bold text-white">{selectedRide.estimated_duration_minutes || '--'}</p>
                  <p className="text-xs text-gray-500">Minutes</p>
                </div>
                <div className="bg-[#0a0a0a] rounded-xl p-4 text-center">
                  <DollarSign className="w-6 h-6 text-green-400 mx-auto mb-2" />
                  <p className="text-xl font-bold text-green-400">
                    ${(selectedRide.driver_earnings || (selectedRide.estimated_fare || 0) * 0.85).toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500">Earnings</p>
                </div>
              </div>

              {/* Payment Method */}
              {selectedRide.payment_method === 'cash' && (
                <div className="bg-amber-500/20 border border-amber-500/30 rounded-xl p-4">
                  <p className="text-amber-400 font-semibold flex items-center gap-2">
                    💵 Cash Payment
                  </p>
                  <p className="text-amber-300/70 text-sm mt-1">
                    Collect ${selectedRide.estimated_fare?.toFixed(2)} from passenger at pickup
                  </p>
                </div>
              )}

              {/* Main Action Button */}
              {status.action && (
                <Button
                  className={`w-full py-6 text-lg font-bold ${
                    selectedRide.status === 'in_progress' 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-[#8B5CF6] hover:bg-[#7C3AED]'
                  }`}
                  onClick={() => {
                    const nextStatus = {
                      driver_assigned: 'en_route_pickup',
                      en_route_pickup: 'arrived_pickup',
                      arrived_pickup: 'in_progress',
                      in_progress: 'completed'
                    };
                    const next = nextStatus[selectedRide.status];
                    if (next) updateRideStatus(selectedRide.id, next);
                  }}
                  disabled={processingAction}
                  data-testid="ride-action-btn"
                >
                  {processingAction ? (
                    <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                  ) : selectedRide.status === 'in_progress' ? (
                    <CheckCircle className="w-5 h-5 mr-2" />
                  ) : (
                    <Play className="w-5 h-5 mr-2" />
                  )}
                  {status.action}
                </Button>
              )}
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-10 h-10 border-2 border-[#8B5CF6] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-400">Loading your rides...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white py-6 px-4 sticky top-0 z-40">
        <div className="flex items-center justify-between mb-4">
          <Link to="/worker/dashboard" className="text-white/80 hover:text-white">
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <h1 className="text-xl font-bold">Driver Rides</h1>
          <button onClick={() => setIsOnline(!isOnline)} className="relative">
            <div className={`w-12 h-6 rounded-full transition-colors ${isOnline ? 'bg-green-500' : 'bg-gray-600'}`}>
              <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-all ${isOnline ? 'right-0.5' : 'left-0.5'}`}></div>
            </div>
          </button>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{stats.today}</p>
            <p className="text-xs text-white/70">Today's Rides</p>
          </div>
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-green-300">${stats.earnings.toFixed(2)}</p>
            <p className="text-xs text-white/70">Today's Earnings</p>
          </div>
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold flex items-center justify-center gap-1">
              <Star className="w-4 h-4 text-yellow-400" fill="currentColor" />
              {stats.rating.toFixed(1)}
            </p>
            <p className="text-xs text-white/70">Rating</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="sticky top-[156px] bg-[#050505] z-30 px-4 py-3 border-b border-white/10">
        <div className="flex gap-2">
          {[
            { id: 'today', label: "Today's Rides", count: todayRides.length },
            { id: 'available', label: 'Available', count: availableRides.length },
            { id: 'history', label: 'History', count: null }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id)}
              className={`flex-1 py-2 px-3 rounded-xl text-sm font-medium transition-all ${
                activeView === tab.id
                  ? 'bg-[#8B5CF6] text-white'
                  : 'bg-[#111111] text-gray-400 hover:text-white'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
              {tab.count !== null && tab.count > 0 && (
                <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${
                  activeView === tab.id ? 'bg-white/20' : 'bg-[#8B5CF6]'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {activeView === 'today' && (
          <div>
            {todayRides.length === 0 ? (
              <div className="text-center py-12">
                <Car className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">No rides scheduled for today</p>
                <p className="text-gray-600 text-sm mt-1">Check available rides or wait for assignments</p>
                <Button 
                  className="mt-4 bg-[#8B5CF6] hover:bg-[#7C3AED]"
                  onClick={() => setActiveView('available')}
                >
                  View Available Rides
                </Button>
              </div>
            ) : (
              todayRides.map(ride => renderRideCard(ride))
            )}
          </div>
        )}

        {activeView === 'available' && (
          <div>
            {!isOnline ? (
              <div className="text-center py-12">
                <Radio className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">You're currently offline</p>
                <p className="text-gray-600 text-sm mt-1">Go online to see available rides</p>
                <Button 
                  className="mt-4 bg-green-600 hover:bg-green-700"
                  onClick={() => setIsOnline(true)}
                >
                  Go Online
                </Button>
              </div>
            ) : availableRides.length === 0 ? (
              <div className="text-center py-12">
                <RefreshCw className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">No rides available right now</p>
                <p className="text-gray-600 text-sm mt-1">Pull down to refresh or wait for new requests</p>
                <Button 
                  variant="outline" 
                  className="mt-4 border-white/20 text-white"
                  onClick={fetchData}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            ) : (
              availableRides.map(ride => renderRideCard(ride, true))
            )}
          </div>
        )}

        {activeView === 'history' && (
          <div>
            {completedRides.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">No completed rides yet</p>
                <p className="text-gray-600 text-sm mt-1">Your ride history will appear here</p>
              </div>
            ) : (
              completedRides.slice(0, 20).map(ride => renderRideCard(ride))
            )}
          </div>
        )}
      </div>

      {/* Selected Ride Modal */}
      {renderRideModal()}

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#111111] border-t border-white/10 px-4 py-3 flex justify-around z-40">
        <Link to="/worker/dashboard" className="flex flex-col items-center text-gray-500 hover:text-white">
          <Home className="w-6 h-6" />
          <span className="text-xs mt-1">Dashboard</span>
        </Link>
        <button className="flex flex-col items-center text-[#8B5CF6]">
          <Car className="w-6 h-6" />
          <span className="text-xs mt-1">Rides</span>
        </button>
        <Link to="/worker/dashboard?tab=earnings" className="flex flex-col items-center text-gray-500 hover:text-white">
          <DollarSign className="w-6 h-6" />
          <span className="text-xs mt-1">Earnings</span>
        </Link>
        <Link to="/worker/dashboard?tab=settings" className="flex flex-col items-center text-gray-500 hover:text-white">
          <Settings className="w-6 h-6" />
          <span className="text-xs mt-1">Settings</span>
        </Link>
      </div>
    </div>
  );
};

export default DriverRidesPage;
